import { ERemoteImportURLType } from '@/types/remote-imports'
import type { TUnsupportedImportHost } from '@/types/remote-imports'

const ANY_SCHEME = /^[a-z][a-z0-9+.-]*:\/\//i

/**
 * Normalize a user-entered import URL, or return null when it is not a usable
 * http(s) URL. Scheme-less input is treated as https; single-label hosts such
 * as `localhost` (no dot) are rejected.
 */
export function normalizeImportUrl(input: string): string | null {
  const trimmed = input.trim()
  if (!trimmed) {
    return null
  }
  const candidate = ANY_SCHEME.test(trimmed) ? trimmed : `https://${trimmed}`
  let parsed: URL
  try {
    parsed = new URL(candidate)
  } catch {
    return null
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return null
  }
  if (!parsed.hostname.includes('.')) {
    return null
  }
  return parsed.href
}

export function isValidImportUrl(input: string): boolean {
  return normalizeImportUrl(input) !== null
}

const youtubeHosts = ['youtube.com', 'youtu.be', 'youtube-nocookie.com', 'youtubekids.com']

const ytDlpVideoHosts = ['vimeo.com', 'facebook.com', 'fb.watch', 'fb.com', 'tiktok.com', 'instagram.com']

const wistiaHosts = ['wistia.com', 'wistia.net', 'wi.st']

function urlHostMatchesAny(url: string, hosts: string[]): boolean {
  let hostname: string
  try {
    hostname = new URL(url.trim()).hostname.toLowerCase()
  } catch {
    return false
  }

  return hosts.some((host) => hostMatches(hostname, host))
}

function isWistiaPageUrl(url: string): boolean {
  return urlHostMatchesAny(url, wistiaHosts) && !new URL(url.trim()).pathname.startsWith('/deliveries/')
}

export function isScheduledVideoUrl(url: string): boolean {
  return urlHostMatchesAny(url, youtubeHosts) || urlHostMatchesAny(url, ytDlpVideoHosts) || isWistiaPageUrl(url)
}

export function detectImportUrlType(url: string): ERemoteImportURLType {
  if (urlHostMatchesAny(url, youtubeHosts)) {
    return ERemoteImportURLType.YouTube
  }

  if (urlHostMatchesAny(url, ytDlpVideoHosts) || isWistiaPageUrl(url)) {
    return ERemoteImportURLType.Video
  }

  const gdocRegex = /^(https?:\/\/)?docs\.google\.com\/document\/.+$/
  if (gdocRegex.test(url)) {
    return ERemoteImportURLType.GDoc
  }

  const gslidesRegex = /^(https?:\/\/)?docs\.google\.com\/presentation\/.+$/
  if (gslidesRegex.test(url)) {
    return ERemoteImportURLType.GSlides
  }

  const oneDriveType = detectOneDriveFileType(url)
  if (oneDriveType) {
    return oneDriveType
  }

  const urlObj = new URL(url)
  const pathname = urlObj.pathname.toLowerCase()

  if (pathname.endsWith('.docx') || pathname.endsWith('.doc')) {
    return ERemoteImportURLType.DocX
  }

  if (pathname.endsWith('.pdf')) {
    return ERemoteImportURLType.Pdf
  }

  if (pathname.endsWith('.pptx')) {
    return ERemoteImportURLType.Pptx
  }

  if (
    pathname.endsWith('.mp3') ||
    pathname.endsWith('.wav') ||
    pathname.endsWith('.m4a') ||
    pathname.endsWith('.aac')
  ) {
    return ERemoteImportURLType.Audio
  }

  if (
    pathname.endsWith('.mp4') ||
    pathname.endsWith('.mov') ||
    pathname.endsWith('.avi') ||
    pathname.endsWith('.wmv')
  ) {
    return ERemoteImportURLType.Video
  }
  return ERemoteImportURLType.Web
}

export function resolveImportUrl(input: string): { url: string; type: ERemoteImportURLType } | null {
  const url = normalizeImportUrl(input)
  if (!url) {
    return null
  }

  return { url, type: detectImportUrlType(url) }
}

const googleDriveFileIdRegex =
  /^https:\/\/drive\.google\.com\/(?:file\/(?:u\/\d+\/)?d\/([A-Za-z0-9_-]+)|(?:open|uc)\?(?:[^#]*&)?id=([A-Za-z0-9_-]+))/

export function getGoogleDriveDownloadUrl(url: string): string | null {
  const match = url.trim().match(googleDriveFileIdRegex)
  const id = match?.[1] ?? match?.[2]
  if (!id) {
    return null
  }

  return `https://drive.google.com/uc?export=download&id=${id}`
}

export function isSameOriginUrl(url: string): boolean {
  try {
    return new URL(url.trim(), window.location.href).origin === window.location.origin
  } catch {
    return false
  }
}

export function getDroppedImportUrl(dataTransfer: DataTransfer | null | undefined): string | null {
  const url = (dataTransfer?.getData('text/uri-list') || dataTransfer?.getData('text/plain'))?.trim()
  if (!url || isSameOriginUrl(url)) {
    return null
  }

  return url
}

const googleDriveNonFileMessage =
  'This link opens Google Drive, not a file. Open the file in Google Drive, click Share, set access to "Anyone with the link" and paste that link.'

const unsupportedImportHosts: TUnsupportedImportHost[] = [
  {
    hosts: ['canva.link', 'canva.com'],
    message: "Canva designs can't be imported from a link. In Canva use Share, then Download as PDF, and upload the file here.",
  },
  {
    hosts: ['acrobat.adobe.com'],
    message: "Adobe Acrobat share links can't be imported. Open the file in Acrobat, download the PDF and upload it here.",
  },
  {
    hosts: ['icloud.com'],
    message: "iCloud links can't be imported. Download the file to your device and upload it here.",
  },
  {
    hosts: ['sharepoint.com'],
    message: "Work or school OneDrive links can't be imported. Open the file, click Download and upload it here.",
  },
  {
    hosts: [
      'chatgpt.com',
      'claude.ai',
      'copilot.microsoft.com',
      'copilot.cloud.microsoft',
      'm365.cloud.microsoft',
      'gamma.app',
      'perplexity.ai',
    ],
    message:
      "Links to AI chats and pages can't be imported. Copy the text into a document, or export it as PDF or DOCX, and upload that file.",
  },
  {
    hosts: ['dropbox.com', 'dropboxusercontent.com'],
    pathPrefixes: ['/preview/', '/home'],
    message: 'This is a private Dropbox link. In Dropbox click Share, then Copy link, and paste that link here.',
  },
  {
    hosts: ['flipsnack.com', 'heyzine.com', 'fliphtml5.com'],
    message: "Flipbook links can't be imported. Download the original PDF and upload it here.",
  },
  {
    hosts: ['word.cloud.microsoft'],
    message:
      "Word for the web links can't be imported. In Word click Share, then Copy link, and paste that link, or download the file and upload it here.",
  },
  {
    hosts: ['camscanner.com'],
    message: "CamScanner links can't be imported. Export the scan as PDF in CamScanner and upload the file here.",
  },
  {
    hosts: ['res.cdn.office.net', 'onecdn.static.microsoft'],
    message: 'This is a file icon, not the file. Download the file from OneDrive and upload it here.',
  },
]

function hostMatches(hostname: string, host: string): boolean {
  return hostname === host || hostname.endsWith(`.${host}`)
}

export function getUnsupportedImportUrlMessage(url: string): string | null {
  let hostname: string
  try {
    hostname = new URL(url.trim()).hostname.toLowerCase()
  } catch {
    return null
  }
  if (hostname === 'drive.google.com' && getGoogleDriveDownloadUrl(url) === null) {
    return googleDriveNonFileMessage
  }
  if (hostname === 'export-download.canva.com') {
    return null
  }

  const pathname = new URL(url.trim()).pathname

  return (
    unsupportedImportHosts.find(
      (rule) =>
        rule.hosts.some((host) => hostMatches(hostname, host)) &&
        (!rule.pathPrefixes || rule.pathPrefixes.some((prefix) => pathname.startsWith(prefix))),
    )?.message ?? null
  )
}

const oneDriveTypesByLetter: Record<string, ERemoteImportURLType> = {
  b: ERemoteImportURLType.Pdf,
  w: ERemoteImportURLType.DocX,
  p: ERemoteImportURLType.Pptx,
  v: ERemoteImportURLType.Video,
}

export function isOneDriveShareUrl(url: string): boolean {
  try {
    return ['1drv.ms', 'onedrive.live.com'].includes(new URL(url.trim()).hostname)
  } catch {
    return false
  }
}

function detectOneDriveFileType(url: string): ERemoteImportURLType | null {
  if (!isOneDriveShareUrl(url)) {
    return null
  }
  const letter = new URL(url.trim()).pathname.match(/^\/(?::)?([a-z])(?::)?\//)?.[1]

  return letter ? (oneDriveTypesByLetter[letter] ?? null) : null
}

export function isCloudFileShareUrl(url: string): boolean {
  return getGoogleDriveDownloadUrl(url) !== null || isOneDriveShareUrl(url)
}
